import { Box, Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { DocumentIcon } from "@heroicons/react/20/solid";
import UploadModal from "../UploadModal";
import StyledSelect from "../../../product/all-products/components/custom/StyledSelect";
import { getAllExpensesLedgers } from "../../../../../config/Services";
import { getStoreCurrencySymbol } from "../../../../../utils/currency";

const AddExpenseModal = ({
  openNewExpense,
  handleClose,
  uploadedFile,
  formData,
  setFormData,
  setUploadedFile,
  openUploadModal,
  setOpenUploadModal,
  handleCreateExpense,
  loader,
}) => {
  const [ledgerOptions, setLedgerOptions] = useState([]);

  // ── NEW: Category options ───────────────────────────────────────────────
  // NOTE: Placeholder options to match the Replit reference UI. If/when a
  // categories API becomes available, replace this static list the same
  // way `ledgerOptions` is fetched below via `fetchAndGroupExpenseLedgers`.
  const [categoryOptions, setCategoryOptions] = useState([
    { label: "Inventory", value: "INVENTORY" },
    { label: "Maintenance", value: "MAINTENANCE" },
    { label: "Repairs", value: "REPAIRS" },
    { label: "Logistics", value: "LOGISTICS" },
    { label: "Advertising and Promotions", value: "ADVERTISING" },
    { label: "Others", value: "OTHERS" },
  ]);

  const orgId = localStorage.getItem("orgId");
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    bgcolor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "20px",
    boxShadow: "0 28px 90px rgba(15, 23, 42, 0.24)",
    p: 0,
    outline: "none",
    overflow: "hidden",
    height:"95vh"
  };

  async function fetchAndGroupExpenseLedgers() {
    try {
      const response = await getAllExpensesLedgers(0, 10, orgId);

      const ledgers = response?.data?.data?.data?.data || [];

      // Group by groupName
      const grouped = ledgers?.reduce((acc, ledger) => {
        const group = ledger?.groupName || "Others";
        if (!acc[group]) acc[group] = [];
        acc[group].push({
          label: ledger?.ledgerDisplayName,
          value: ledger?.ledgerId,
        });
        return acc;
      }, {});

      const formattedGroups = Object.entries(grouped)?.map(([groupName, options]) => ({
        label: groupName,
        options,
      }));

      return formattedGroups;
    } catch (error) {
      return [];
    }
  }

  useEffect(() => {
    async function loadLedgers() {
      const data = await fetchAndGroupExpenseLedgers();
      setLedgerOptions(data);
    }
    loadLedgers();
  }, []);

  const handleFileUpload = (uploadedFileData) => {
    // uploadedFileData now contains: { name, url, size, type }
    setUploadedFile(uploadedFileData);
    setOpenUploadModal(false);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const handleChange = (value, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleClearAll = () => {
    setFormData({
      expenseTitle: "",
      amount: "",
      reason: "",
      ledger: {},
      category: {},
    });
    setUploadedFile(null);
  };

  return (
    <div>
      <Modal open={openNewExpense} onClose={handleClose} aria-labelledby="add-expense-modal">
        <Box sx={style}>
          <div className="expense-modal-inner-container">
            <h2 className="expense-modal-title">
              {formData?.voucherEntryId || formData?.voucherReferenceId
                ? "Edit Expense"
                : "Add New Expense"}
            </h2>

            <div className="expense-modal-card-container">
              <div className="expense-modal-grid">
                {/* Field 1: Title */}
                <div className="expense-modal-field">
                  <label className="expense-modal-label">
                    Expense Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    placeholder="e.g. Vegetable restock"
                    type="text"
                    className="expense-modal-input"
                    name="expenseTitle"
                    value={formData?.expenseTitle}
                    onChange={(e) => handleChange(e.target.value, "expenseTitle")}
                    required
                    data-testid="input-expense-title"
                  />
                </div>

                {/* Field 2 & 3: Amount & Category (matches Replit 2-col row) */}
                <div className="expense-modal-row-2col">
                  {/* Amount */}
                  <div className="expense-modal-field">
                    <label className="expense-modal-label">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="expense-modal-amount-input-wrapper">
                      <span className="expense-modal-amount-currency">{getStoreCurrencySymbol()}</span>
                      <input
                        placeholder="0"
                        type="number"
                        className="expense-modal-input expense-modal-amount-input"
                        name="amount"
                        value={formData?.amount}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            handleChange(val, "amount");
                          }
                        }}
                        required
                        data-testid="input-expense-amount"
                      />
                    </div>
                  </div>

                  {/* Category (NEW: matches Replit reference) */}
                  <div className="expense-modal-field">
                    <label className="expense-modal-label">Category</label>
                    <StyledSelect
                      onChange={(e) => handleChange(e, "category")}
                      value={formData?.category}
                      options={categoryOptions}
                      autoFocus={false}
                      className="expense-modal-select"
                      placeholder="Select category..."
                      data-testid="select-expense-category"
                      borderRadius={"20px"}
                    />
                  </div>
                </div>

                {/* Field: Reason for Expense (moved to its own full-width row, still inside card) */}
                <div className="expense-modal-field">
                  <label className="expense-modal-label">Reason for Expense</label>
                  <input
                    placeholder="Brief description of this expense"
                    type="text"
                    className="expense-modal-input"
                    name="reason"
                    value={formData?.reason}
                    onChange={(e) => handleChange(e.target.value, "reason")}
                    data-testid="input-expense-reason"
                  />
                </div>

                {/* Field: Expense Ledger (moved OUTSIDE the card container, matches Replit reference mt-5 spacing) */}
                <div className="expense-modal-field mt-5">
                  <label className="expense-modal-label">Expense Ledger</label>
                  <StyledSelect
                    onChange={(e) => handleChange(e, "ledger")}
                    value={formData?.ledger}
                    options={ledgerOptions}
                    autoFocus={false}
                    className="expense-modal-select"
                    placeholder="Select ledger..."
                    data-testid="select-expense-ledger"
                    maxMenuHeight={150}
                  />
                </div>

                {/* Field: Bill Upload (moved OUTSIDE the card container, matches Replit reference mt-8 spacing) */}
                <div className="expense-modal-field mt-8">
                  <label className="expense-modal-label font-semibold">Bill Upload</label>

                  {uploadedFile && (
                    <div className="expense-file-upload-box" style={{ cursor: "pointer" }}>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                        onClick={() => {
                          if (uploadedFile?.url) {
                            window.open(uploadedFile?.url, "_blank");
                          }
                        }}
                      >
                        <DocumentIcon style={{ width: "20px", height: "20px", color: "#6b7280" }} />
                        <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                          {uploadedFile?.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "0.25rem",
                          display: "flex",
                          alignItems: "center",
                          color: "#6b7280",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                      </button>
                    </div>
                  )}

                  <button
                    className="expense-modal-upload-btn"
                    onClick={() => setOpenUploadModal(true)}
                    data-testid="button-upload-expense-bill"
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-upload"
                      aria-hidden="true"
                      style={{ width: "16px", height: "16px", marginRight: "8px" }}
                    >
                      <path d="M12 3v12"></path>
                      <path d="m17 8-5-5-5 5"></path>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    </svg>
                    Upload
                  </button>
                </div>
              </div>
            </div>

            {/* Actions: Submit */}
            <div className="expense-modal-footer">
              <button
                className="expense-modal-submit-btn"
                onClick={handleCreateExpense}
                disabled={loader}
                data-testid="button-submit-expense"
                type="button"
              >
                Submit
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-arrow-right"
                  aria-hidden="true"
                  style={{ width: "16px", height: "16px", marginLeft: "8px" }}
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>

          {/* Absolute close button */}
          <button
            type="button"
            className="expense-modal-close-btn-absolute"
            onClick={handleClose}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-x"
              aria-hidden="true"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </Box>
      </Modal>
      <UploadModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        onUpload={handleFileUpload}
      />
    </div>
  );
};

export default AddExpenseModal;
